import is from '@sindresorhus/is';
import type { RefObject } from 'react';
import { useCallback, useRef } from 'react';

type UseInfiniteScrollOptions = {
    readonly hasNextPage: boolean;
    readonly isFetchingNextPage: boolean;
    readonly onLoadMore: () => void;
    readonly root: RefObject<HTMLDivElement | null>;
};

type UseInfiniteScrollReturn = {
    readonly observerRef: (node: HTMLElement | null) => void;
};

export const useInfiniteScroll = ({
    hasNextPage,
    isFetchingNextPage,
    onLoadMore,
    root,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
    const observer = useRef<IntersectionObserver | null>(null);

    const observerRef = useCallback(
        (node: HTMLElement | null) => {
            if (!is.null(observer.current)) {
                observer.current.disconnect();
            }

            if (is.null(node) || isFetchingNextPage) {
                return;
            }

            observer.current = new IntersectionObserver(
                (entries) => {
                    const firstEntry = entries[0];
                    if (!is.undefined(firstEntry) && firstEntry.isIntersecting && hasNextPage && !isFetchingNextPage) {
                        onLoadMore();
                    }
                },
                {
                    root: root.current,
                    threshold: 0.1,
                },
            );

            observer.current.observe(node);
        },
        [hasNextPage, isFetchingNextPage, onLoadMore, root],
    );

    return { observerRef };
};
